// Review marks in the TinyMCE body: the active finding's text, an
// addition's ghost sentence, and fixes applied this session.
//
// Marks are spans TinyMCE treats as bogus: data-mce-bogus="1" spans are
// unwrapped and "all" spans dropped when TinyMCE serializes, so marks never
// reach getContent() or the saved card. They are added straight to the DOM,
// outside the undo stack, so they make no undo step and no edit.

const MARK_ATTR = 'data-ne-mark';

// Text nodes under `root` (ghost marks have none: CSS draws their text).
function textNodes(root) {
    const doc = root.ownerDocument;
    const walker = doc.createTreeWalker(root, 4 /* NodeFilter.SHOW_TEXT */);
    const nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);
    return nodes;
}

// The body's text with whitespace runs folded into one space, and where
// each character came from.
function foldedText(nodes) {
    const chars = [];
    const places = [];
    for (const node of nodes) {
        const value = node.nodeValue;
        for (let offset = 0; offset < value.length; offset += 1) {
            const isSpace = /\s/.test(value[offset]);
            if (isSpace && chars[chars.length - 1] === ' ') continue;
            chars.push(isSpace ? ' ' : value[offset]);
            places.push({ node, offset });
        }
    }
    return { text: chars.join(''), places };
}

const fold = text => text.replace(/\s+/g, ' ').trim();

// The `occurrence`-th place `text` appears in `root`, as { start, end }
// text positions ({ node, offset }), or null.
export function findText(root, text, occurrence = 0) {
    const needle = fold(text || '');
    if (!root || !needle) return null;
    const { text: haystack, places } = foldedText(textNodes(root));
    let at = -1;
    for (let n = 0; n <= occurrence; n += 1) {
        at = haystack.indexOf(needle, at + 1);
        if (at === -1) return null;
    }
    const last = places[at + needle.length - 1];
    return { start: places[at], end: { node: last.node, offset: last.offset + 1 } };
}

function markSpan(doc, kind) {
    const span = doc.createElement('span');
    span.setAttribute(MARK_ATTR, kind);
    span.setAttribute('data-mce-bogus', '1');
    span.className = `ne-mark ne-mark-${kind}`;
    return span;
}

// Wrap the text between `start` and `end` in mark spans, one per text node.
function wrap(root, found, kind) {
    const { start, end } = found;
    const nodes = textNodes(root);
    const first = nodes.indexOf(start.node);
    const lastIndex = nodes.indexOf(end.node);
    const spans = [];
    for (let i = first; i <= lastIndex; i += 1) {
        let node = nodes[i];
        const from = i === first ? start.offset : 0;
        const to = i === lastIndex ? end.offset : node.nodeValue.length;
        if (from >= to) continue;
        if (to < node.nodeValue.length) node.splitText(to);
        if (from > 0) node = node.splitText(from);
        const span = markSpan(node.ownerDocument, kind);
        node.parentNode.insertBefore(span, node);
        span.appendChild(node);
        spans.push(span);
    }
    return spans;
}

// Mark `text` in the editor body. Returns the mark spans (empty when the
// text is not there).
export function markText(editor, text, kind, occurrence = 0) {
    const body = editor?.getBody?.();
    const found = findText(body, text, occurrence);
    return found ? wrap(body, found, kind) : [];
}

// Show `text` as ghost text right after `afterSpan`. The text is drawn by
// CSS (::after), so it is no text node: word count and search skip it.
export function insertGhost(afterSpan, text) {
    const doc = afterSpan.ownerDocument;
    const ghost = doc.createElement('span');
    ghost.setAttribute(MARK_ATTR, 'ghost');
    ghost.setAttribute('data-mce-bogus', 'all');
    ghost.setAttribute('contenteditable', 'false');
    ghost.className = 'ne-mark ne-mark-ghost';
    ghost.setAttribute('data-ghost', ` ${text}`);
    afterSpan.parentNode.insertBefore(ghost, afterSpan.nextSibling);
    return ghost;
}

// Remove marks of the given kinds ('active', 'ghost', 'applied').
export function clearMarks(editor, kinds) {
    const body = editor?.getBody?.();
    if (!body) return;
    for (const mark of body.querySelectorAll(`[${MARK_ATTR}]`)) {
        const kind = mark.getAttribute(MARK_ATTR);
        if (!kinds.includes(kind)) continue;
        const parent = mark.parentNode;
        if (kind === 'ghost') {
            mark.remove();
        } else {
            while (mark.firstChild) parent.insertBefore(mark.firstChild, mark);
            mark.remove();
        }
        parent.normalize();
    }
}

// How many times `text` appears in `textBefore` (the body's text before a
// finding's anchor), so the mark lands on the right copy of a phrase the
// card repeats.
export function occurrenceBefore(textBefore, text) {
    const needle = fold(text || '');
    const haystack = fold(textBefore || '');
    if (!needle) return 0;
    let count = 0;
    let at = haystack.indexOf(needle);
    while (at !== -1) {
        count += 1;
        at = haystack.indexOf(needle, at + 1);
    }
    return count;
}

// The CSS the marks need inside the editor's iframe.
export const MARK_STYLES = [
    '.ne-mark-active{background:#e0e7ff;border-radius:2px;box-shadow:0 0 0 2px #e0e7ff}',
    '.ne-mark-applied{background:#f0fdf4;border-radius:2px;box-shadow:0 0 0 2px #f0fdf4}',
    '.ne-mark-ghost::after{content:attr(data-ghost);color:#6d28d9;background:#f5f3ff;border-radius:2px}',
].join('');
